/**
 * CR313: All Flags Tab
 * 
 * Two-column layout with category rail and sections.
 */

import React, { useRef, useEffect, useState, useCallback } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { FeatureFlagsCategoryRail } from './FeatureFlagsCategoryRail';
import { FeatureFlagRow } from './FeatureFlagRow';
import { FeatureFlagDrawer } from './FeatureFlagDrawer';
import { FEATURE_FLAG_CATEGORIES, type FeatureFlagCategory } from '@/lib/admin/featureFlagsRegistry';
import type { FeatureFlagState, UseFeatureFlagsAdminResult } from '@/hooks/admin/useFeatureFlagsAdmin';
import type { StatusFilter, TypeFilter } from './FeatureFlagsHeader';

interface FeatureFlagsAllTabProps {
  adminHook: UseFeatureFlagsAdminResult;
  search: string;
  categoryFilter: FeatureFlagCategory | 'all';
  typeFilter: TypeFilter;
  statusFilter: StatusFilter;
}

function filterFlags(
  flags: FeatureFlagState[],
  search: string,
  categoryFilter: FeatureFlagCategory | 'all',
  typeFilter: TypeFilter,
  statusFilter: StatusFilter
): FeatureFlagState[] {
  return flags.filter(flag => {
    const entry = flag.registryEntry;
    if (!entry) return false;
    
    // Search
    if (search) {
      const q = search.toLowerCase();
      if (!entry.label.toLowerCase().includes(q) && !entry.key.toLowerCase().includes(q)) {
        return false;
      }
    }
    
    // Category
    if (categoryFilter !== 'all' && entry.category !== categoryFilter) {
      return false;
    }
    
    // Type
    if (typeFilter !== 'all' && entry.type !== typeFilter) {
      return false;
    }
    
    // Status
    if (statusFilter === 'default' && flag.isOverridden) return false;
    if (statusFilter === 'overridden' && !flag.isOverridden) return false;
    
    return true;
  });
}

export function FeatureFlagsAllTab({
  adminHook,
  search,
  categoryFilter,
  typeFilter,
  statusFilter,
}: FeatureFlagsAllTabProps) {
  const { flags, unregisteredFlags, draftValues, updateDraft, deleteOverride } = adminHook;
  
  const [activeCategory, setActiveCategory] = useState<FeatureFlagCategory | 'Other' | null>(null);
  const [showUnregistered, setShowUnregistered] = useState(false);
  const [drawerFlag, setDrawerFlag] = useState<FeatureFlagState | null>(null);
  
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  const filteredFlags = filterFlags(flags, search, categoryFilter, typeFilter, statusFilter);
  
  // Group filtered flags by category
  const flagsByCategory = new Map<FeatureFlagCategory, FeatureFlagState[]>();
  for (const cat of FEATURE_FLAG_CATEGORIES) {
    flagsByCategory.set(cat, []);
  }
  for (const flag of filteredFlags) {
    const cat = flag.registryEntry?.category;
    if (cat) {
      flagsByCategory.get(cat)?.push(flag);
    }
  }
  
  // IntersectionObserver for active section highlight
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the most visible section
        let bestEntry: IntersectionObserverEntry | null = null;
        let bestRatio = 0;
        
        for (const entry of entries) {
          if (entry.isIntersecting && entry.intersectionRatio > bestRatio) {
            bestRatio = entry.intersectionRatio;
            bestEntry = entry;
          }
        }
        
        if (bestEntry) {
          const cat = bestEntry.target.getAttribute('data-category');
          if (cat) {
            setActiveCategory(cat as FeatureFlagCategory | 'Other');
          }
        }
      },
      {
        root: container,
        rootMargin: '-20% 0px -60% 0px',
        threshold: [0, 0.25, 0.5, 0.75, 1],
      }
    );
    
    sectionRefs.current.forEach(el => observer.observe(el));
    
    return () => observer.disconnect();
  }, [filteredFlags.length]);
  
  const handleCategoryClick = useCallback((category: FeatureFlagCategory | 'Other') => {
    const el = sectionRefs.current.get(category);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  
  const handleDeleteOverride = async () => {
    if (!drawerFlag) return;
    const success = await deleteOverride(drawerFlag.key);
    if (success) {
      setDrawerFlag(null);
    }
  };
  
  const setSectionRef = useCallback((category: string) => (el: HTMLDivElement | null) => {
    if (el) {
      sectionRefs.current.set(category, el);
    } else {
      sectionRefs.current.delete(category);
    }
  }, []);
  
  return (
    <div className="flex h-full">
      {/* Category Rail */}
      <FeatureFlagsCategoryRail
        flags={flags}
        activeCategory={activeCategory}
        showUnregistered={showUnregistered}
        unregisteredCount={unregisteredFlags.length}
        onCategoryClick={handleCategoryClick}
        onToggleUnregistered={() => {
          setShowUnregistered(!showUnregistered);
          if (!showUnregistered) {
            handleCategoryClick('Other');
          }
        }}
      />
      
      {/* Main content */}
      <div ref={scrollContainerRef} className="flex-1 overflow-auto">
        <div className="p-4 space-y-6">
          {FEATURE_FLAG_CATEGORIES.map(category => {
            const categoryFlags = flagsByCategory.get(category) || [];
            if (categoryFlags.length === 0 && categoryFilter === 'all') return null;
            if (categoryFlags.length === 0) return null;
            
            const defaultCount = categoryFlags.filter(f => !f.isOverridden).length;
            const overriddenCount = categoryFlags.filter(f => f.isOverridden).length;
            
            return (
              <section
                key={category}
                ref={setSectionRef(category)}
                data-category={category}
                className="scroll-mt-4"
              >
                {/* Section header */}
                <div className="flex items-center gap-3 mb-2 px-4">
                  <h2 className="text-lg font-semibold">{category}</h2>
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Badge variant="secondary" className="font-normal">{categoryFlags.length}</Badge>
                    <span>total</span>
                    <span className="mx-1">·</span>
                    <span>{defaultCount} default</span>
                    <span className="mx-1">·</span>
                    <span>{overriddenCount} overridden</span>
                  </div>
                </div>
                
                {/* Flags table */}
                <div className="border rounded-lg overflow-hidden">
                  {categoryFlags.map(flag => (
                    <FeatureFlagRow
                      key={flag.key}
                      flag={flag}
                      draftValue={draftValues.get(flag.key)}
                      onValueChange={(value) => updateDraft(flag.key, value)}
                      onOpenDrawer={() => setDrawerFlag(flag)}
                    />
                  ))}
                </div>
              </section>
            );
          })}
          
          {/* Unregistered section */}
          {showUnregistered && unregisteredFlags.length > 0 && (
            <section
              ref={setSectionRef('Other')}
              data-category="Other"
              className="scroll-mt-4"
            >
              <div className="flex items-center gap-3 mb-2 px-4">
                <h2 className="text-lg font-semibold text-muted-foreground">Other (Unregistered)</h2>
                <Badge variant="outline">{unregisteredFlags.length}</Badge>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                {unregisteredFlags.map(({ key, value }) => (
                  <div key={key} className="flex items-center gap-4 px-4 py-3 border-b last:border-b-0 bg-muted/20">
                    <div className="flex-1 min-w-0">
                      <code className="text-sm font-mono">{key}</code>
                    </div>
                    <code className="text-xs text-muted-foreground truncate max-w-xs">
                      {JSON.stringify(value)}
                    </code>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
      
      {/* Drawer */}
      <FeatureFlagDrawer
        flag={drawerFlag}
        draftValue={drawerFlag ? draftValues.get(drawerFlag.key) : undefined}
        open={!!drawerFlag}
        onOpenChange={(open) => !open && setDrawerFlag(null)}
        onValueChange={(value) => drawerFlag && updateDraft(drawerFlag.key, value)}
        onDeleteOverride={handleDeleteOverride}
      />
    </div>
  );
}
